import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../../api/client';

const APPROVER_TYPES = ['role', 'user', 'manager', 'dept_head', 'auto'];
const COND_OPS       = ['==', '!=', '>', '<', '>=', '<='];
const COND_ACTIONS   = ['require', 'skip', 'escalate'];

const EMPTY_STEP = { sequence: '', name: '', approverType: 'role', approverRef: '', parallelGroup: '', slaHours: '' };
const EMPTY_COND = { field: '', op: '==', value: '', action: 'require' };

// Conditions store their expression as JSON (string or object depending on the
// driver); render defensively into a readable `field op value` form.
function renderExpr(cond) {
  let e = cond.expression;
  if (typeof e === 'string') {
    try { e = JSON.parse(e); } catch { return e; }
  }
  if (e && typeof e === 'object') return `${e.field} ${e.op} ${e.value}`;
  return '';
}

export default function ProcessBuilderPage() {
  const { id } = useParams();
  const [def,     setDef]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  // Add-step modal
  const [stepOpen,   setStepOpen]   = useState(false);
  const [stepForm,   setStepForm]   = useState(EMPTY_STEP);
  const [stepSaving, setStepSaving] = useState(false);

  // Add-condition modal (scoped to a step)
  const [condStepId, setCondStepId] = useState(null);
  const [condForm,   setCondForm]   = useState(EMPTY_COND);
  const [condSaving, setCondSaving] = useState(false);

  // Activate
  const [activating, setActivating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/bp/definitions/${id}`);
      setDef(data.data?.definition || null);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // --- Add step ---
  function patchStep(key, val) { setStepForm((f) => ({ ...f, [key]: val })); }

  async function handleAddStep(e) {
    e.preventDefault();
    setStepSaving(true);
    setError(null);
    try {
      const body = {
        sequence:     Number(stepForm.sequence),
        name:         stepForm.name,
        approverType: stepForm.approverType,
      };
      if (stepForm.approverRef)   body.approverRef   = stepForm.approverRef;
      if (stepForm.parallelGroup) body.parallelGroup = stepForm.parallelGroup;
      if (stepForm.slaHours)      body.slaHours      = Number(stepForm.slaHours);
      await api.post(`/bp/definitions/${id}/steps`, body);
      setStepForm(EMPTY_STEP);
      setStepOpen(false);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setStepSaving(false);
    }
  }

  // --- Add condition ---
  function patchCond(key, val) { setCondForm((f) => ({ ...f, [key]: val })); }

  function openCondModal(stepId) {
    setCondForm(EMPTY_COND);
    setCondStepId(stepId);
  }

  async function handleAddCond(e) {
    e.preventDefault();
    setCondSaving(true);
    setError(null);
    try {
      await api.post(`/bp/steps/${condStepId}/conditions`, {
        expression: { field: condForm.field, op: condForm.op, value: condForm.value },
        action:     condForm.action,
      });
      setCondForm(EMPTY_COND);
      setCondStepId(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setCondSaving(false);
    }
  }

  // --- Activate ---
  async function handleActivate() {
    if (!confirm('Activate this process definition? Steps can no longer be edited.')) return;
    setActivating(true);
    setError(null);
    try {
      await api.post(`/bp/definitions/${id}/activate`);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setActivating(false);
    }
  }

  if (loading) return <div data-testid="bp-builder-page" data-loading="true"><p>Loading...</p></div>;
  if (!def)    return <div data-testid="bp-builder-page" data-loading="false"><p style={{ color: 'red' }}>{error || 'Definition not found.'}</p></div>;

  // Order steps and cluster steps sharing a parallel_group into visual lanes.
  const steps = [...(def.steps || [])].sort((a, b) => a.sequence - b.sequence);
  const groups = [];
  const groupIndex = new Map();
  for (const s of steps) {
    const key = s.parallel_group ? `pg:${s.parallel_group}` : `single:${s.id}`;
    if (groupIndex.has(key)) {
      groups[groupIndex.get(key)].steps.push(s);
    } else {
      groupIndex.set(key, groups.length);
      groups.push({ key, parallelGroup: s.parallel_group || null, steps: [s] });
    }
  }

  const statusColor = def.status === 'active' ? '#137333' : def.status === 'retired' ? '#a50e0e' : '#8a6d00';

  return (
    <div data-testid="bp-builder-page" data-loading={loading ? 'true' : 'false'}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div>
          <h2 data-testid="bp-builder-title" style={{ marginBottom: '0.25rem' }}>{def.name}</h2>
          <div style={{ color: '#666', fontSize: '0.9rem' }}>
            <code>{def.key}</code> &nbsp;·&nbsp; {def.resource_type} &nbsp;·&nbsp; v{def.version}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span
            data-testid="bp-status-badge"
            style={{ padding: '0.2rem 0.6rem', borderRadius: 12, background: statusColor, color: '#fff', fontSize: '0.8rem', textTransform: 'capitalize' }}
          >
            {def.status}
          </span>
          {def.status === 'draft' && (
            <button data-testid="bp-activate" onClick={handleActivate} disabled={activating}>
              {activating ? 'Activating...' : 'Activate'}
            </button>
          )}
        </div>
      </div>

      {error && <p style={{ color: 'red' }}>{error}</p>}

      {def.status === 'draft' && (
        <div style={{ marginBottom: '1rem' }}>
          <button data-testid="bp-step-add" onClick={() => { setStepForm(EMPTY_STEP); setStepOpen(true); }}>
            Add Step
          </button>
        </div>
      )}

      {/* Visual step column */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
        {groups.map((g, gi) => (
          <div key={g.key} style={{ width: '100%', maxWidth: 640, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {g.parallelGroup && (
              <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.25rem' }}>
                Parallel group: <strong>{g.parallelGroup}</strong>
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.75rem', width: '100%', justifyContent: 'center' }}>
              {g.steps.map((s) => (
                <div
                  key={s.id}
                  data-testid={`bp-step-card-${s.id}`}
                  style={{ flex: 1, border: '1px solid #ccc', borderRadius: 8, padding: '0.9rem', background: '#fafafa' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <strong>#{s.sequence} · {s.name}</strong>
                    {s.sla_hours != null && <span style={{ fontSize: '0.75rem', color: '#666' }}>SLA {s.sla_hours}h</span>}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#444', marginTop: '0.35rem' }}>
                    Approver: <strong>{s.approver_type}</strong>{s.approver_ref ? ` (${s.approver_ref})` : ''}
                  </div>

                  {/* Conditions */}
                  {(s.conditions && s.conditions.length > 0) && (
                    <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.1rem', fontSize: '0.8rem' }}>
                      {s.conditions.map((c) => (
                        <li key={c.id} data-testid={`bp-condition-${c.id}`}>
                          <em>{c.action}</em> when <code>{renderExpr(c)}</code>
                        </li>
                      ))}
                    </ul>
                  )}

                  {def.status === 'draft' && (
                    <div style={{ marginTop: '0.6rem' }}>
                      <button
                        data-testid="bp-condition-add"
                        onClick={() => openCondModal(s.id)}
                        style={{ fontSize: '0.8rem' }}
                      >
                        Add Condition
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {gi < groups.length - 1 && <div style={{ color: '#999', margin: '0.25rem 0' }}>↓</div>}
          </div>
        ))}
        {!groups.length && <p>No steps yet. Add the first approval step to build the process.</p>}
      </div>

      {/* Add-step modal */}
      {stepOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 8, padding: '1.5rem', minWidth: 380 }}>
            <h3>Add Step</h3>
            <form onSubmit={handleAddStep}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <input
                  placeholder="Sequence (e.g. 1)"
                  type="number"
                  min={0}
                  value={stepForm.sequence}
                  onChange={(e) => patchStep('sequence', e.target.value)}
                  required
                />
                <input
                  placeholder="Step name"
                  value={stepForm.name}
                  onChange={(e) => patchStep('name', e.target.value)}
                  required
                />
                <label>
                  Approver Type
                  <select value={stepForm.approverType} onChange={(e) => patchStep('approverType', e.target.value)}>
                    {APPROVER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>
                <input
                  placeholder="Approver Ref (role/user id, optional)"
                  value={stepForm.approverRef}
                  onChange={(e) => patchStep('approverRef', e.target.value)}
                />
                <input
                  placeholder="Parallel Group (optional)"
                  value={stepForm.parallelGroup}
                  onChange={(e) => patchStep('parallelGroup', e.target.value)}
                />
                <input
                  placeholder="SLA Hours (optional)"
                  type="number"
                  min={1}
                  value={stepForm.slaHours}
                  onChange={(e) => patchStep('slaHours', e.target.value)}
                />
              </div>
              {error && <p style={{ color: 'red', marginTop: '0.5rem' }}>{error}</p>}
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                <button type="submit" data-testid="bp-step-form-submit" disabled={stepSaving}>
                  {stepSaving ? 'Adding...' : 'Add Step'}
                </button>
                <button type="button" onClick={() => { setStepOpen(false); setError(null); }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add-condition modal */}
      {condStepId && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 8, padding: '1.5rem', minWidth: 380 }}>
            <h3>Add Condition</h3>
            <form onSubmit={handleAddCond}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <input
                  placeholder="Field (e.g. amount_cents)"
                  value={condForm.field}
                  onChange={(e) => patchCond('field', e.target.value)}
                  required
                />
                <label>
                  Operator
                  <select value={condForm.op} onChange={(e) => patchCond('op', e.target.value)}>
                    {COND_OPS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </label>
                <input
                  placeholder="Value"
                  value={condForm.value}
                  onChange={(e) => patchCond('value', e.target.value)}
                  required
                />
                <label>
                  Action
                  <select value={condForm.action} onChange={(e) => patchCond('action', e.target.value)}>
                    {COND_ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                </label>
              </div>
              {error && <p style={{ color: 'red', marginTop: '0.5rem' }}>{error}</p>}
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                <button type="submit" data-testid="bp-condition-form-submit" disabled={condSaving}>
                  {condSaving ? 'Adding...' : 'Add Condition'}
                </button>
                <button type="button" onClick={() => { setCondStepId(null); setError(null); }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
